<template>
    <section class="section announcer-board-app">
        <div class="container-fluid">
            <AnnouncerBoardHeader />

            <AnnouncerBoardOptions />

            <FightsList v-if="store.initDone" />
            <div v-else class="columns is-centered is-mobile">
                <LoadingSpinner2 class="has-added-margin" />
            </div>
        </div>

        <CalledCompetitorsBar />

        <AdminCategorySidebar
            :event-id="eventId"
            ref="categorySidebar"
            :event-timezone="eventTimezone"
            :current-user-id="currentUserId"
            :can-view-roster="canViewRoster"
        />
    </section>
</template>

<script setup lang="ts">
    import { getCalledCompetitorsSessionStoreKey, useAnnouncerBoardStore } from "./announcerBoardStore";
    import { onMounted, onUnmounted, ref, watch } from "vue";
    import FightsList from "./FightsList.vue";
    import { clearIntervalAsync, setIntervalAsync } from "set-interval-async";
    import history from "../Common/history";
    import { createClient } from "../Common/websocketClient";
    import AnnouncerBoardHeader from "./AnnouncerBoardHeader.vue";
    import AnnouncerBoardOptions from "./AnnouncerBoardOptions.vue";
    import { handleError } from "../Common/errorHandler";
    import LoadingSpinner2 from "../Common/LoadingSpinner2.vue";
    import {
        CALL_OF_THE_COMPETITOR_CHANNEL,
        extractMatIdFromChannel,
        getAnnouncerBoardCallCountUpdatesChannel,
        getCalledCompetitorsChannel,
        getScoreboardMatChannel,
        SCOREBOARD_CHANNEL,
    } from "../Common/websocketUtils";
    import CalledCompetitorsBar from "./CalledCompetitorsBar.vue";
    import sessionStore from "../Common/sessionStore";
    import AdminCategorySidebar from "../Common/Fights/AdminCategorySidebar.vue";
    import { ComponentExposed } from "vue-component-type-helpers";
    import { OPEN_BRACKET_FIGHTS_OVER_VIEW_SIDEBAR, singleFightEventBus } from "../Common/Fights/singleFightEvents";

    const historyInstance = history.getInstance(window);
    const props = defineProps(["eventId", "eventTimezone", "currentUserId", "canViewRoster"]);
    const store = useAnnouncerBoardStore();

    store.eventId = props.eventId;
    store.eventTimezone = props.eventTimezone;

    // SYNCING SELECTED OPTIONS

    store.matsPerPage = Number(historyInstance.getQueryParam("matsPerPage")) || 6;
    store.fightsLimit = Number(historyInstance.getQueryParam("limit")) || 15;
    store.pageNumber = Number(historyInstance.getQueryParam("pageNumber")) || 0;

    store.selectedMatIds = (historyInstance.getQueryParam("mat") || "")
        .split(",")
        .filter(v => !!v)
        .map(Number);
    store.searchQuery = historyInstance.getQueryParam("searchQuery") || "";

    watch(
        () => store.selectedMatIds,
        () => {
            historyInstance.setQueryParam("mat", store.selectedMatIds.join(","));
        },
    );
    watch(
        () => store.pageNumber,
        () => {
            historyInstance.setQueryParam("pageNumber", store.pageNumber);
        },
    );
    watch(
        () => store.fightsLimit,
        () => {
            historyInstance.setQueryParam("limit", store.fightsLimit);
        },
    );
    watch(
        () => store.matsPerPage,
        () => {
            historyInstance.setQueryParam("matsPerPage", store.matsPerPage);
        },
    );
    watch(
        () => store.searchQuery,
        () => {
            historyInstance.setQueryParam("searchQuery", store.searchQuery);
        },
    );
    // TIMER HANDLING

    let interval: ReturnType<typeof setIntervalAsync> | null = null;

    const timerHandler = async () => {
        if (store.timer === 1) {
            store.pageNumber++;
            await store.refreshAllMats().catch(handleError);
            store.timer = 20;
            return;
        }
        store.timer--;
    };

    function initializeTimerLoop() {
        if (!interval) {
            interval = setIntervalAsync(timerHandler, 1000);
            store.isTimerRunning = true;
        }
    }

    function stopTimerLoop() {
        if (interval) {
            clearIntervalAsync(interval);
            interval = null;
        }
    }

    watch(
        () => store.isTimerRunning,
        () => {
            if (store.isTimerRunning) {
                initializeTimerLoop();
            } else {
                stopTimerLoop();
            }
        },
    );

    onUnmounted(() => {
        stopTimerLoop();
    });

    // WEBSOCKETS

    const websocketClient = createClient();
    store.websocketClient = websocketClient;
    websocketClient.connect(null); //Not providing channel. Let's iterate over all mats below and join channels.

    function joinAllChannelsForMatId(matId: number) {
        websocketClient.joinChannel(getScoreboardMatChannel(matId));
        websocketClient.joinChannel(getCalledCompetitorsChannel(matId));
    }

    function leaveAllChannelsForMatId(matId: number) {
        websocketClient.leaveChannel(getScoreboardMatChannel(matId));
        websocketClient.leaveChannel(getCalledCompetitorsChannel(matId));
    }

    websocketClient.onopen = () => {
        store.selectedMatIds.forEach(matId => {
            joinAllChannelsForMatId(matId);
        });
        websocketClient.joinChannel(getAnnouncerBoardCallCountUpdatesChannel(store.eventId));
    };

    websocketClient.onmessage = message => {
        const data = JSON.parse(message.data);

        if (!data.channel) {
            return;
        }

        if (data.channel === getAnnouncerBoardCallCountUpdatesChannel(store.eventId)) {
            store.refreshCallCountHistory().catch(handleError);
            return;
        }

        if (data.fightId && data.channel.includes(SCOREBOARD_CHANNEL)) {
            store.trackedTimeForFights[data.fightId] = data.internalTime;
            return;
        }
        if (data.channel.includes(CALL_OF_THE_COMPETITOR_CHANNEL)) {
            store.addCallOfCompetitorForMat(data);
            return;
        }
    };

    watch(
        () => store.matsForGivenPage,
        () => {
            const currentChannels = Array.from(websocketClient.getChannelsSet());
            const currentMatIds = new Set(currentChannels.map(channel => extractMatIdFromChannel(channel)).filter(id => id !== null));
            const newMatIds = store.matsForGivenPage.map(mat => mat.id);
            const removedMatIds = Array.from(currentMatIds).filter(channel => !newMatIds.includes(channel));

            removedMatIds.forEach(matId => {
                leaveAllChannelsForMatId(matId);
            });
            newMatIds.forEach(matId => {
                joinAllChannelsForMatId(matId);
            });
        },
    );

    onUnmounted(() => {
        websocketClient.close();
    });

    // Watching calls of the competitors

    const fromSessionStorage = sessionStore.get(getCalledCompetitorsSessionStoreKey(store.eventId));

    store.calledCompetitors = JSON.parse(fromSessionStorage || "[]");

    // Init of application

    onMounted(async () => {
        await Promise.all([store.refreshAllMats(), store.refreshCallCountHistory()])
            .then(() => {
                initializeTimerLoop();
            })
            .catch(handleError)
            .finally(() => {
                store.initDone = true;
            });
    });

    // Sidebar handling
    const categorySidebar = ref<ComponentExposed<typeof AdminCategorySidebar> | null>(null);

    onMounted(() => {
        singleFightEventBus.$on(OPEN_BRACKET_FIGHTS_OVER_VIEW_SIDEBAR, (parameterId: number) => {
            categorySidebar.value?.open(parameterId);
        });
    });
</script>

<style scoped></style>
